/**
 * QnaAntwortComposer — the answer textarea + send button for one Rückfragen thread (P2 Placement
 * Slice 9, Tab 3c). The label is «Antwort senden» for an open thread, «Nachricht senden» otherwise.
 * A missing mailbox shows the hint beside the button; a send error maps to a human line.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { QNA_MAILBOX_HINWEIS, QNA_NICHT_VERSANDT_HINWEIS, QNA_TEXT_MAX } from '@brokly/shared';

const ERROR_TEXT: Record<string, string> = {
  mailbox_not_connected: QNA_MAILBOX_HINWEIS,
  mailbox_reauth_required: QNA_MAILBOX_HINWEIS,
  mail_send_blocked: 'Versand durch die Mail-Richtlinie blockiert.',
  mailbox_send_failed: 'Postfach-Provider hat den Versand abgelehnt.',
  versand_laeuft: 'Versand läuft bereits.',
  empfaenger_fehlt: 'Keine Empfängeradresse bekannt.',
  empfaenger_bestaetigung_required: 'Bitte den abweichenden Empfänger bestätigen.',
  submission_not_sent: QNA_NICHT_VERSANDT_HINWEIS,
};

export function QnaAntwortComposer({
  onSend,
  disabled,
  sending,
  mailboxVerbunden,
  error,
  sendLabel = 'Antwort senden',
  empfaenger,
  empfaengerAbweichend = false,
  carrierDomain,
}: {
  onSend: (text: string, opts: { empfaenger_bestaetigt_an?: string }) => void;
  disabled?: boolean;
  sending?: boolean;
  mailboxVerbunden: boolean;
  error?: string | null;
  sendLabel?: string;
  empfaenger?: string | null;
  empfaengerAbweichend?: boolean;
  carrierDomain?: string | null;
}) {
  const [text, setText] = useState('');
  const [bestaetigt, setBestaetigt] = useState(false);
  // Any recipient change clears the tick, also when an earlier address comes back.
  const [shownFor, setShownFor] = useState(empfaenger);
  if (shownFor !== empfaenger) {
    setShownFor(empfaenger);
    setBestaetigt(false);
  }
  const empty = text.trim().length === 0;
  const errorLine = error ? ERROR_TEXT[error] ?? 'Versand fehlgeschlagen.' : null;
  const confirmBlocked = empfaengerAbweichend && !bestaetigt;

  return (
    <div className="flex flex-col gap-2" data-testid="qna-composer">
      {empfaenger && (
        <p className="text-xs text-muted-foreground" data-testid="qna-composer-empfaenger">
          An: {empfaenger}
        </p>
      )}
      {empfaengerAbweichend && (
        <div className="flex flex-col gap-1 rounded-md border border-status-warning/40 bg-status-warning/10 p-2">
          <p className="text-xs text-status-warning">
            Empfaenger liegt nicht auf der Versicherer-Domain{carrierDomain ? ` (${carrierDomain})` : ''}.
          </p>
          <label className="flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={bestaetigt}
              onChange={(e) => setBestaetigt(e.target.checked)}
              data-testid="qna-composer-empfaenger-confirm"
            />
            An diese Adresse senden
          </label>
        </div>
      )}
      <textarea
        className="min-h-[88px] rounded-md border border-border bg-background p-2 text-sm"
        value={text}
        maxLength={QNA_TEXT_MAX}
        placeholder="Antwort an den Versicherer…"
        disabled={disabled || sending}
        onChange={(e) => setText(e.target.value)}
        data-testid="qna-composer-input"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground tabular-nums">
          {text.length} / {QNA_TEXT_MAX}
        </span>
        <div className="flex items-center gap-2">
          {!mailboxVerbunden && <span className="text-xs text-muted-foreground">{QNA_MAILBOX_HINWEIS}</span>}
          <Button
            disabled={disabled || sending || empty || !mailboxVerbunden || confirmBlocked}
            onClick={() => onSend(text.trim(), { empfaenger_bestaetigt_an: empfaengerAbweichend ? empfaenger ?? undefined : undefined })}
            data-testid="qna-composer-send"
          >
            {sending ? 'Wird gesendet …' : sendLabel}
          </Button>
        </div>
      </div>
      {errorLine && (
        <p className="text-xs text-status-danger" data-testid="qna-composer-error">
          {errorLine}
        </p>
      )}
    </div>
  );
}
